/**
 * 👤 User Service — kullanıcı verisinin TEK KAPISI
 *
 * Profil, tercih, rıza, avatar, itibar puanı ve etkinlik günlüğü sorguları
 * yedi ayrı ekranın içinde doğrudan Supabase çağrısı olarak duruyordu
 * (14 çağrı). Bir React bileşeninin içindeki sorgu test edilemez: çalıştırmak
 * için React, router ve oturum gerekir.
 *
 * ESKİ HÂLİ ÜÇ KATMANDI: `userService` → `UserUseCases` →
 * `SupabaseUserRepository`. Ortadaki iki katman saf devretme yapıyordu,
 * hiçbir şey eklemiyorlardı. Zincir kesildi; sorgular burada.
 *
 * Yan fayda: DI kabı importu kalktığı için `uuid` (saf ESM) artık bu
 * modülün import grafiğinde değil. Onu dolaylı çeken her test
 * "Must use import to load ES Module" ile çöküyordu.
 *
 * Davranışları `userService.test.ts` çiviliyor.
 */

import { supabase } from '@/lib/supabase'
import { logError } from '@/lib/log'

type Kayit = Record<string, unknown>

/**
 * Üretilmiş Supabase tipleri bu iki veritabanı fonksiyonunu tanımıyor
 * (`check_profile_complete_bonus`, `process_referral`); imza burada
 * daraltılıyor. Tipler yeniden üretilirse bu köprü kaldırılabilir.
 */
type RpcCagri = (
  ad: string,
  param: Kayit
) => Promise<{ data: unknown; error: { message: string } | null }>

// ============================================
// PROFİL
// ============================================

/** Kullanıcıyı IBAN listesiyle birlikte tek sorguda okur. */
export async function getProfileWithIbans(
  userId: string
): Promise<{ data: Kayit | null; error: string | null }> {
  if (!userId) return { data: null, error: 'Kullanıcı kimliği boş' }

  const { data, error } = await supabase
    .from('users')
    .select('*, ibans:user_ibans(*)')
    .eq('id', userId)
    .maybeSingle()

  if (error) {
    logError('[userService] profil okunamadı:', error)
    return { data: null, error: error.message }
  }
  // EŞDEĞER MUTASYON: `??` yerine `||` davranışı DEĞİŞTİRMEZ. Hata yolu
  // yukarıda dönüldü; `data` ya nesne ya `null`, nesne hep doğru-değerli.
  // Bu dosyada aynı desenin iki örneği daha var (tercih okuma, hesap
  // ekleme); gerekçe üçünde de aynı.
  // `maybeSingle` bulunamayınca hata değil null döner — "henüz kayıt yok".
  return { data: (data as Kayit) ?? null, error: null }
}


/**
 * Grup limiti kontrolü için gereken üç alan.
 * `signal` verilirse sorgu iptal edilebilir (ekran kapanınca).
 */
export async function getProfileSummary(
  userId: string,
  signal?: AbortSignal
): Promise<{ data: Kayit | null; error: string | null }> {
  if (!userId) return { data: null, error: 'Kullanıcı kimliği boş' }

  let sorgu = supabase
    .from('users')
    .select('full_name, pro_tier, is_pro')
    .eq('id', userId)

  if (signal) sorgu = sorgu.abortSignal(signal)

  const { data, error } = await sorgu.single()
  if (error) {
    logError('[userService] profil özeti okunamadı:', error)
    return { data: null, error: error.message }
  }
  return { data: (data as Kayit) ?? null, error: null }
}

/** Ücretli üyelik durumu. Okunamazsa FALSE — ücretli sayılmaz. */
export async function isPro(userId: string): Promise<boolean> {
  if (!userId) return false

  const { data, error } = await supabase
    .from('users')
    .select('is_pro')
    .eq('id', userId)
    .single()

  if (error || !data) return false
  return Boolean((data as { is_pro: boolean | null }).is_pro)
}

/** Görünen ad. Yoksa null. */
export async function getFullName(userId: string): Promise<string | null> {
  if (!userId) return null

  const { data, error } = await supabase
    .from('users')
    .select('full_name')
    .eq('id', userId)
    .single()

  if (error || !data) return null
  return (data as { full_name: string | null }).full_name ?? null
}
/**
 * Ad ve avatar kaynakları (kendi yüklediği, Google, Apple).
 *
 * Onboarding hangi kaynağın var olduğuna bakıp yedek avatar üretiyor;
 * Home ise sosyal avatarı eşitlemek için okuyor. `full_name` ikisinin de
 * işine yarıyor, tek sorguda geliyor.
 */
export interface AvatarSources {
  full_name: string | null
  avatar_url: string | null
  google_avatar_url: string | null
  apple_avatar_url: string | null
}

export async function getAvatarSources(
  userId: string
): Promise<{ data: AvatarSources | null; error: string | null }> {
  if (!userId) return { data: null, error: 'Kullanıcı kimliği boş' }

  const { data, error } = await supabase
    .from('users')
    .select('full_name, avatar_url, google_avatar_url, apple_avatar_url')
    .eq('id', userId)
    .maybeSingle()

  if (error) {
    logError('[userService] avatar kaynakları okunamadı:', error)
    return { data: null, error: error.message }
  }
  return { data: (data as unknown as AvatarSources) ?? null, error: null }
}

/** Profilin belirli alanlarını günceller. */
export async function updateProfileFields(
  userId: string,
  updates: Kayit
): Promise<{ error: string | null }> {
  if (!userId) return { error: 'Kullanıcı kimliği boş' }
  // Alan verilmeden `update({})` anlamsız bir yazma denemesi olurdu.
  if (!updates || Object.keys(updates).length === 0) return { error: 'Güncellenecek alan yok' }

  const { error } = await supabase.from('users').update(updates).eq('id', userId)
  if (error) {
    logError('[userService] profil güncellenemedi:', error)
    return { error: error.message }
  }
  return { error: null }
}

/** İtibar puanı. Okunamazsa `null` — sıfır DEĞİL; bilinmiyor ile 0 ayrılır. */
export async function getReputationScore(userId: string): Promise<number | null> {
  if (!userId) return null

  const { data, error } = await supabase
    .from('users')
    .select('reputation_score')
    .eq('id', userId)
    .single()

  if (error || !data) return null
  return (data as { reputation_score: number }).reputation_score ?? null
}

/**
 * Son giriş kaydı. Profil ekranı buradan konum bilgisini okuyor.
 */
export async function getLastLogin(
  userId: string
): Promise<{ data: Kayit | null; error: string | null }> {
  if (!userId) return { data: null, error: 'Kullanıcı kimliği boş' }

  const { data, error } = await supabase
    .from('activity_logs')
    .select('*')
    .eq('user_id', userId)
    .eq('action', 'login')
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle()

  if (error) {
    logError('[userService] son giriş okunamadı:', error)
    return { data: null, error: error.message }
  }
  return { data: (data as Kayit) ?? null, error: null }
}

/** Tek bir profil alanını günceller, zaman damgasını da yazar. */
export async function updateProfileField(
  userId: string,
  field: string,
  value: unknown
): Promise<{ error: string | null }> {
  if (!field) return { error: 'Alan adı boş' }
  return updateProfileFields(userId, {
    [field]: value,
    updated_at: new Date().toISOString(),
  })
}

// ============================================
// TERCİHLER
// ============================================

/**
 * Kullanıcı tercihleri. Satırın kendisini değil, İÇİNDEKİ `preferences`
 * nesnesini döner — çağrı yerlerinin ikisi de onu istiyor.
 */
export async function getPreferences(
  userId: string,
  signal?: AbortSignal
): Promise<{ data: Kayit | null; error: string | null }> {
  if (!userId) return { data: null, error: 'Kullanıcı kimliği boş' }

  let sorgu = supabase.from('user_preferences').select('*').eq('user_id', userId)
  if (signal) sorgu = sorgu.abortSignal(signal)

  const { data, error } = await sorgu.maybeSingle()

  if (error) {
    logError('[userService] tercihler okunamadı:', error)
    return { data: null, error: error.message }
  }
  return { data: ((data as Kayit | null)?.preferences as Kayit) ?? null, error: null }
}

/**
 * Tercihleri yazar.
 *
 * `onConflict: 'user_id'` açıkça yazılı: `user_id` zaten birincil anahtar
 * olduğu için davranış aynı, ama niyet okunur oluyor.
 */
export async function savePreferences(
  userId: string,
  preferences: Kayit
): Promise<{ error: string | null }> {
  if (!userId) return { error: 'Kullanıcı kimliği boş' }

  const { error } = await supabase.from('user_preferences').upsert(
    {
      user_id: userId,
      // Uretilmis tip `Json` bekliyor; serbest nesne oraya daraliyor.
      preferences: preferences as never,
      updated_at: new Date().toISOString(),
    },
    { onConflict: 'user_id' }
  )

  if (error) {
    logError('[userService] tercihler yazılamadı:', error)
    return { error: error.message }
  }
  return { error: null }
}

// ============================================
// YASAL RIZA
// ============================================

/**
 * Rıza kaydı yazar.
 *
 * Eksik parametreyle YAZILMAZ: yasal bir kaydı eksik bilgiyle oluşturmak,
 * hiç oluşturmamaktan kötüdür.
 */
export async function recordConsent(
  userId: string,
  consentType: string,
  version: string = '1.0'
): Promise<{ error: string | null }> {
  if (!userId) return { error: 'Kullanıcı kimliği boş' }
  if (!consentType) return { error: 'Rıza türü boş' }

  const { error } = await supabase.from('user_consents').upsert({
    user_id: userId,
    consent_type: consentType,
    version,
  })

  if (error) {
    logError('[userService] rıza kaydedilemedi:', error)
    return { error: error.message }
  }
  return { error: null }
}

// ============================================
// PUAN VE DAVET (veritabanı fonksiyonları)
// ============================================

export interface ProfileBonusResult {
  awarded: boolean
  change?: number
  newScore?: number
  reason?: string
}

/** Profil tamamlama bonusunu ister. Puanı veren yer veritabanı fonksiyonu. */
export async function claimProfileCompletionBonus(userId: string): Promise<ProfileBonusResult> {
  const { data, error } = await (supabase.rpc as unknown as RpcCagri)('check_profile_complete_bonus', {
    p_user_id: userId,
  })

  if (error || !data) {
    if (error) logError('[userService] profil bonusu alınamadı:', error)
    return { awarded: false }
  }
  return data as unknown as ProfileBonusResult
}

export interface ReferralResult {
  success: boolean
  message?: string
  referrer_name?: string
}

/** Davet kodunu kullanır. Kod kırpılır ve BÜYÜK harfe çevrilir. */
export async function redeemReferralCode(code: string): Promise<ReferralResult> {
  const temiz = code.trim().toUpperCase()
  if (!temiz) return { success: false, message: 'Davet kodu boş olamaz' }

  const { data, error } = await (supabase.rpc as unknown as RpcCagri)('process_referral', {
    p_referral_code: temiz,
  })

  if (error) {
    logError('[userService] davet kodu işlenemedi:', error)
    return { success: false, message: 'Davet kodu işlenemedi' }
  }
  return data as unknown as ReferralResult
}

// ============================================
// ETKİNLİK GÜNLÜĞÜ
// ============================================

/** Etkinlik günlüğüne kayıt yazar. */
export async function logActivity(
  userId: string,
  action: string,
  metadata: Kayit = {}
): Promise<{ error: string | null }> {
  if (!userId || !action) return { error: 'Kullanıcı ya da eylem boş' }

  const { error } = await supabase.from('activity_logs').insert({
    user_id: userId,
    action,
    // Üretilmiş tip `Json` bekliyor; serbest nesne oraya daralıyor.
    metadata: metadata as never,
  })

  if (error) {
    logError('[userService] etkinlik yazılamadı:', error)
    return { error: error.message }
  }
  return { error: null }
}

// ============================================
// AVATAR
// ============================================

export const AVATAR_UPLOAD_FAILED_MESSAGE =
  'Profil fotoğrafın şu an yüklenemedi. Lütfen biraz sonra tekrar dene.'

/**
 * Profil fotoğrafını yükler ve genel adresini döner.
 *
 * `now` parametresi dosya adındaki zaman damgası için; verilmezse şimdi.
 * Test edilebilirlik için ayrıldı, sahada davranış aynı.
 */
export async function uploadAvatar(
  userId: string,
  file: File,
  now: number = Date.now()
): Promise<{ url: string | null; error: string | null }> {
  if (!userId) return { url: null, error: 'Kullanıcı kimliği boş' }

  const uzanti = file.name.split('.').pop() || 'png'
  const yol = `users/${userId}/avatar-${now}.${uzanti}`

  const kova = supabase.storage.from('avatars')
  // Yol zaman damgasıyla benzersiz; üzerine yazma yok. `upsert: true` depoda
  // INSERT'e ek olarak SELECT + UPDATE izni de isterdi (GUNUM-VAR-1W).
  const { error: uploadError } = await kova.upload(yol, file, {
    cacheControl: '3600',
    upsert: false,
  })

  if (uploadError) {
    logError('[userService] avatar yüklenemedi:', uploadError)
    // Ham depo metni ("row-level security policy") kullanıcıya gösterilmez.
    return { url: null, error: AVATAR_UPLOAD_FAILED_MESSAGE }
  }

  const {
    data: { publicUrl },
  } = kova.getPublicUrl(yol)
  return { url: publicUrl, error: null }
}


// ============================================
// BANKA HESAPLARI (IBAN)
//
// `MyBankAccounts` üç sorguyu doğrudan yapıyordu.
// ============================================

/** Yeni hesap ekler ve eklenen kaydı geri okur (ekran listeyi onunla günceller). */
export async function createIban(
  userId: string,
  currency: string,
  iban: string
): Promise<{ data: Kayit | null; error: string | null }> {
  const temizIban = (iban || '').trim()
  if (!userId) return { data: null, error: 'Kullanıcı kimliği boş' }
  if (!temizIban) return { data: null, error: 'IBAN boş olamaz' }

  const { data, error } = await supabase
    .from('user_ibans')
    .insert({ user_id: userId, currency, iban: temizIban, bank_name: null } as never)
    .select()
    .single()

  if (error) {
    logError('[userService] hesap eklenemedi:', error)
    return { data: null, error: error.message }
  }
  return { data: (data as Kayit) ?? null, error: null }
}

/** Var olan hesabı günceller ve güncel kaydı geri okur. */
export async function updateIban(
  ibanId: string,
  currency: string,
  iban: string
): Promise<{ data: Kayit | null; error: string | null }> {
  const temizIban = (iban || '').trim()
  if (!ibanId) return { data: null, error: 'Hesap kimliği boş' }
  if (!temizIban) return { data: null, error: 'IBAN boş olamaz' }

  const { data, error } = await supabase
    .from('user_ibans')
    .update({ currency, iban: temizIban } as never)
    .eq('id', ibanId)
    .select()
    .single()

  if (error) {
    logError('[userService] hesap güncellenemedi:', error)
    return { data: null, error: error.message }
  }
  return { data: (data as Kayit) ?? null, error: null }
}

/** Hesabı siler. Kimlik boşsa SORGU YAPILMAZ. */
export async function deleteIban(ibanId: string): Promise<{ error: string | null }> {
  if (!ibanId) return { error: 'Hesap kimliği boş' }

  const { error } = await supabase.from('user_ibans').delete().eq('id', ibanId)
  if (error) {
    logError('[userService] hesap silinemedi:', error)
    return { error: error.message }
  }
  return { error: null }
}

export interface ReferralInfo {
  referralCode: string | null
  totalReferrals: number
}

/** Davet kodu ve başarılı davet sayısı. Okunamazsa null. */
export async function getReferralInfo(userId: string): Promise<ReferralInfo | null> {
  if (!userId) return null

  const { data, error } = await supabase
    .from('users')
    .select('referral_code, total_successful_referrals')
    .eq('id', userId)
    .single()

  if (error || !data) return null

  const k = data as { referral_code: string | null; total_successful_referrals: number | null }
  return {
    referralCode: k.referral_code,
    totalReferrals: k.total_successful_referrals || 0,
  }
}
